/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable no-nested-ternary */
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import Dropdown from '@/components/Dropdown/Dropdown';
import Icon from '@/components/Icons/Icon';
import Link from '@/components/Link/Link';
import Modal from '@/components/Modal/Modal';
import RadioGroup from '@/components/RadioGroup/RadioGroup';
import Table from '@/components/Tables/Table/Table';
import { useBundleGroupsPointPrice } from '@/services/bundles/bundles.service';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import classNames from 'classnames';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './EditBundleSettingsModal.module.css';
import { EditBundleSettingsModalProps } from './EditBundleSettingsModal.types';
import { LinkedBundle } from './LinkedBundle';
import { MissingItemsAlert } from '../MissingItemsAlert/MissingItemsAlert';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';

const EditBundleSettingsModal: FC<EditBundleSettingsModalProps> = ({
  isOpen,
  onClose,
  priceOptions,
  selectedRows,
  selectedPrice,
  setSelectedPrice,
  availablePrices,
  recommendedPrices,
  onConfirm,
  isLoading,
  modalDefaultSettings,
  redeemableOnline,
  setRedeemableOnline,
  visibleOnline,
  setVisibleOnline,
  dropdownPriceOptions,
  setDropdownPriceOptions,
  isLinkedBundle,
  isLockPrice,
  isLockRedeemableOnline,
  isLockVisibleOnline,
  lockRedeemableOnline,
  lockVisibleOnline,
  bundleId,
  setSelectedLinkedBundleGroups,
}) => {
  const { t } = useTranslation();
  const [isClicked, setIsClicked] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTooltipInfo, setShowTooltipInfo] = useState(false);
  const [showPriceOptionModal, setShowPriceOptionModal] = useState(false);
  const [otherAvailablePrice, setOtherAvailablePrice] = useState<string | undefined>(undefined);
  const [showDetailPriceOptionModal, setShowDetailPriceOptionModal] = useState(false);
  const hasInactive = selectedRows.some((item) => !item.chosenPrice);
  const hasMissingItems = selectedRows.some((item) => item.isVariantMissing);
  const { data: bundleGroups = [] } = useBundleGroupsPointPrice(isLockPrice || false, bundleId, selectedRows);
  const storeGroupsBundleActive = bundleGroups?.length > 0;
  const [singleValue, setSingleValue] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (isLockPrice) {
      setSelectedLinkedBundleGroups(bundleGroups);
    }

    const hasOneElement = bundleGroups.length === 1;

    if (hasOneElement) {
      setSingleValue(bundleGroups?.at(0)?.chosenPrice);
    } else {
      setSingleValue(undefined);
    }
  }, [bundleGroups, isLockPrice, setSelectedLinkedBundleGroups]);

  //count of recommended prices, minus 1 to account for deactivate option in dropdown
  const recommendedPricesCount = !dropdownPriceOptions ? 0 : dropdownPriceOptions.length - 1;
  const defaultDropdownPriceOptions = [{ value: '', label: t('common.select') }, ...(dropdownPriceOptions || [])];

  const recommendedPriceList = recommendedPrices?.reduce((acc: string[], curr) => {
    acc.push(`$${curr?.toFixed(2) || '-'}`);
    return acc;
  }, []);
  const availableOptions = availablePrices?.map((price) => `$${price.toFixed(2)}`);

  const availablePriceOptions = availableOptions
    ?.filter((price) => !recommendedPriceList.includes(price))
    .map((availablePrice, index) => ({
      label: availablePrice,
      id: index.toString(),
    }));

  const handleClick = (event?: { preventDefault?: () => void }) => {
    event?.preventDefault?.();
    // Open More Price Options when there are prices outside the primary list (e.g. Live NP
    // with only nationalDefaultPrice in the dropdown but bundle recommendedPrices as Other).
    // Otherwise keep the Formstack tip when the primary dropdown has fewer than 4 prices.
    const hasOtherAvailablePrices = (availablePriceOptions?.length ?? 0) > 0;
    if (!hasOtherAvailablePrices && recommendedPricesCount < 4) setShowTooltipInfo(true);
    setIsClicked(true);
    setShowEditModal(true);
  };

  const handleClickPriceDetail = () => {
    setShowDetailPriceOptionModal(!showDetailPriceOptionModal);
  };

  const handlePriceSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedPrice(event.target.value);
    // Deactivating forces both online settings off, the same way answering No to
    // Redeemable Online does. Mixed selections have no default to pre-fill these,
    // so leaving Visible Online unset would keep Confirm disabled.
    if (event.target.value === 'deactivate') {
      setRedeemableOnline(false);
      setVisibleOnline(false);
    }
  };

  const containerClassName =
    redeemableOnline === false || isLockVisibleOnline ? styles.radioGroupContainerDisabled : styles.radioGroupContainer;

  const redeemableOnlineLockStyles =
    isLockRedeemableOnline || (visibleOnline === true && redeemableOnline === true && isLockVisibleOnline)
      ? styles.radioGroupContainerDisabled
      : styles.radioGroupContainer;

  const redeemableLockIcon = isLockRedeemableOnline && (
    <LockOutlinedIcon
      color="primary"
      sx={{
        width: '20px',
        height: '20px',
        marginRight: '5px',
        marginBottom: '-2px',
      }}
    />
  );

  const visibleLockIcon = isLockVisibleOnline && (
    <LockOutlinedIcon
      color="primary"
      sx={{
        width: '20px',
        height: '20px',
        marginRight: '5px',
        marginBottom: '-2px',
      }}
    />
  );

  const onRedeemableOnlineRadioChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.target.id === 'redeemable_yes') {
      setRedeemableOnline(true);
    } else {
      setRedeemableOnline(false);
      setVisibleOnline(false);
    }
  };

  const onVisibleOnlineRadioChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.target.id === 'visible_yes') {
      setVisibleOnline(true);
    } else {
      setVisibleOnline(false);
    }
  };

  const onOtherAvailablePriceRadioChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setOtherAvailablePrice(availablePriceOptions?.filter((item) => item.id === event.target.id)[0].label.slice(1));
  };

  const closeSelectPriceModal = () => {
    setIsClicked(false);
    setShowPriceOptionModal(false);
    setOtherAvailablePrice(undefined);
  };

  const handleOtherAvailablePriceSelect = () => {
    setDropdownPriceOptions([
      { value: otherAvailablePrice || '', label: `$${otherAvailablePrice}` || '' },
      ...priceOptions,
    ]);
    setSelectedPrice(otherAvailablePrice);
    closeSelectPriceModal();
  };

  const isReemableOnlineSelected = () => {
    let reemableOnlineSelected;
    if (!isLockRedeemableOnline) {
      if (redeemableOnline === undefined) {
        reemableOnlineSelected = undefined;
      } else {
        reemableOnlineSelected = redeemableOnline ? 'redeemable_yes' : 'redeemable_no';
      }
    } else {
      reemableOnlineSelected = lockRedeemableOnline ? 'redeemable_yes' : 'redeemable_no';
    }
    return reemableOnlineSelected;
  };

  const isVisibleOnlineSelected = () => {
    let visibleOnlineSelected;
    if (!isLockVisibleOnline) {
      if (redeemableOnline === false) {
        visibleOnlineSelected = 'visible_no';
      } else if (visibleOnline === undefined) {
        visibleOnlineSelected = undefined;
      } else {
        visibleOnlineSelected = visibleOnline ? 'visible_yes' : 'visible_no';
      }
    } else {
      visibleOnlineSelected = lockVisibleOnline ? 'visible_yes' : 'visible_no';
    }
    return visibleOnlineSelected;
  };

  const checkVisibility = () => {
    let constraint01 = false;
    let constraint02: false | undefined | boolean = false;

    // Confirm stays disabled when nothing changed — same rule for normal and NP freeze bundles.
    // Live NP: availablePrices = national + bundle recommended (Other Available uses recommended).
    const selectedPriceNumber = Number(selectedPrice);
    const isSelectedPriceUnavailable =
      selectedPrice !== 'deactivate' && !availablePrices?.some((price) => Number(price) === selectedPriceNumber);
    constraint01 =
      isSelectedPriceUnavailable ||
      (selectedPrice === undefined && redeemableOnline === undefined && visibleOnline === undefined) ||
      (isLockVisibleOnline && selectedPrice && redeemableOnline === undefined) ||
      (isLockRedeemableOnline && selectedPrice && visibleOnline === undefined) ||
      (isLockVisibleOnline && isLockRedeemableOnline && isLockPrice) ||
      // Deactivating a selection that is already inactive changes nothing.
      (selectedPrice === 'deactivate' && modalDefaultSettings?.pricePoint === 'null') ||
      (selectedPrice === modalDefaultSettings?.pricePoint &&
        redeemableOnline === modalDefaultSettings?.redeemableOnline &&
        visibleOnline === modalDefaultSettings?.visibleOnline);

    if (
      isLinkedBundle &&
      selectedPrice !== undefined &&
      redeemableOnline !== undefined &&
      visibleOnline !== undefined &&
      isLockPrice
    ) {
      constraint02 = redeemableOnline === modalDefaultSettings?.redeemableOnline && isLockPrice && isLinkedBundle;
      return constraint01 && constraint02;
    }
    if (
      isLinkedBundle &&
      (selectedPrice !== undefined || visibleOnline === undefined || redeemableOnline === undefined) &&
      isLockPrice
    ) {
      if (selectedPrice !== undefined && visibleOnline !== undefined && redeemableOnline !== undefined) {
        return false;
      }

      if (isLockRedeemableOnline || isLockVisibleOnline) {
        if (isLockRedeemableOnline && redeemableOnline === undefined) {
          setRedeemableOnline(lockRedeemableOnline);
        }

        if (isLockVisibleOnline && visibleOnline === undefined) {
          setVisibleOnline(lockVisibleOnline);
        }

        if (selectedPrice !== undefined && visibleOnline !== undefined && redeemableOnline !== undefined) {
          return false;
        }
      }

      return true;
    }
    if (
      isLinkedBundle &&
      bundleGroups?.length > 0 &&
      selectedPrice === undefined &&
      visibleOnline !== undefined &&
      redeemableOnline !== undefined &&
      isLockPrice
    ) {
      const initialRedeemableValue = modalDefaultSettings?.redeemableOnline !== undefined;

      if (redeemableOnline !== initialRedeemableValue || visibleOnline !== undefined) {
        return false;
      }
      return true;
    }
    if (selectedPrice === undefined || visibleOnline === undefined || redeemableOnline === undefined) {
      if (isLockRedeemableOnline || isLockVisibleOnline) {
        if (isLockRedeemableOnline && redeemableOnline === undefined) {
          setRedeemableOnline(lockRedeemableOnline);
        }

        if (isLockVisibleOnline && visibleOnline === undefined) {
          setVisibleOnline(lockVisibleOnline);
        }

        if (selectedPrice !== undefined && visibleOnline !== undefined && redeemableOnline !== undefined) {
          return false;
        }
      }
      return true;
    }
    return constraint01;
  };

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {!showPriceOptionModal && (
        <Modal
          className={styles.editBundleModal}
          isOpen={isOpen}
          onClose={onClose}
          headerContent={
            <div>
              <div className={styles.headerContentLabel}>{t('editBundle.bundleSettings.editModal.header')}</div>
              {isLinkedBundle && <LinkedBundle />}
            </div>
          }
          showTitleDivider
          maxWidth="610px"
          bodyContent={
            <div className={styles.body}>
              <div className={styles.currentSettings}>
                <div className={styles.currentSettingsTitle}>
                  {t('editBundle.bundleSettings.editModal.currentSettings')}
                </div>
                <Table
                  includeMinWidth={false}
                  height={selectedRows.length > 3 ? '173px' : undefined}
                  data={selectedRows}
                  headersToWrap={['visibleOnline', 'redeemOnline']}
                  columns={[
                    { id: 'group', accessorKey: 'groupName', header: t('editBundle.bundleSettings.table.storeGroup') },
                    {
                      id: 'pricePoint',
                      header: t('editBundle.bundleSettings.table.pricePoint'),
                      accessorFn: (originalRow) =>
                        !originalRow.chosenPrice ? t('common.inactive') : `$${originalRow.chosenPrice.toFixed(2)}`,
                    },
                    {
                      id: 'redeemOnline',
                      header: t('editBundle.bundleSettings.table.redeemOnline'),
                      accessorFn: (originalRow) =>
                        !originalRow.chosenPrice
                          ? '--'
                          : originalRow.chosenRedeemableOnline
                          ? t('common.yes')
                          : t('common.no'),
                    },
                    {
                      id: 'visibleOnline',
                      header: t('editBundle.bundleSettings.table.visibleOnline'),
                      accessorFn: (originalRow) =>
                        !originalRow.chosenPrice
                          ? '--'
                          : originalRow.chosenVisibleOnline
                          ? t('common.yes')
                          : t('common.no'),
                    },
                  ]}
                />
              </div>
              <div className={styles.bodyDivider} />
              <div className={styles.newSettings}>
                <div className={styles.newSettingsTitle}>New Settings</div>
                <div className={styles.dropdownAndRadioGroupsWrapper}>
                  <div className={styles.dropdownContainer}>
                    {isLockPrice && storeGroupsBundleActive ? (
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div className={styles.settingsLabel}>
                          {t('editBundle.bundleSettings.editModal.pricePoint')}
                        </div>

                        {hasInactive ? (
                          <>
                            <div>
                              <div>
                                {singleValue ? (
                                  <div className={styles.lockTextInfo}> ${singleValue.toFixed(2)}</div>
                                ) : (
                                  <Link to="" className={styles.lockTextInfo} onClick={handleClickPriceDetail}>
                                    {t('editBundle.bundleSettings.editModal.priceLockedDetail')}
                                  </Link>
                                )}
                              </div>
                            </div>

                            <div style={{ display: 'flex' }}>
                              <LockOutlinedIcon
                                color="primary"
                                sx={{ width: '20px', height: '20px', paddingTop: '0px' }}
                              />
                              <div className={styles.lockTextInfoDetail}>
                                {t('editBundle.bundleSettings.editModal.priceLocked')}
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div>
                              <div>
                                {singleValue ? (
                                  <div className={styles.lockTextInfo}> ${singleValue.toFixed(2)}</div>
                                ) : (
                                  <Link to="" className={styles.lockTextInfo} onClick={handleClickPriceDetail}>
                                    {t('editBundle.bundleSettings.editModal.priceLockedDetail')}
                                  </Link>
                                )}
                              </div>
                            </div>

                            <div style={{ display: 'flex' }}>
                              <LockOutlinedIcon
                                color="primary"
                                sx={{ width: '20px', height: '20px', paddingTop: '0px' }}
                              />
                              <div className={styles.lockTextInfoDetail}>
                                {t('editBundle.bundleSettings.editModal.priceLocked')}
                              </div>
                            </div>
                          </>
                        )}
                        {showDetailPriceOptionModal && (
                          <Modal
                            className={styles.editBundleModal}
                            isOpen={isOpen}
                            onClose={onClose}
                            headerContent={
                              <div>
                                <div className={styles.headerContentLabel}>Bundle Price Assignment</div>
                                <div className={styles.headerContentLabelDetail}>
                                  For linked bundles with a price lock in place,
                                  <br /> it will be activated with the price of the bundles it is linked to.
                                </div>
                              </div>
                            }
                            showTitleDivider
                            maxWidth="610px"
                            bodyContent={
                              <div className={styles.body}>
                                <Table
                                  includeMinWidth={false}
                                  height={bundleGroups.length > 10 ? '415px' : undefined}
                                  data={bundleGroups}
                                  columns={[
                                    {
                                      id: 'group',
                                      accessorKey: 'groupName',
                                      header: t('editBundle.bundleSettings.table.storeGroup'),
                                    },
                                    {
                                      id: 'pricePoint',
                                      header: t('editBundle.bundleSettings.table.pricePoint'),
                                      accessorFn: (originalRow) =>
                                        !originalRow.chosenPrice
                                          ? t('common.inactive')
                                          : `$${originalRow.chosenPrice.toFixed(2)}`,
                                    },
                                  ]}
                                />
                              </div>
                            }
                            footerContent={
                              <div className={styles.footer}>
                                <Button
                                  variant={ButtonVariants.Secondary}
                                  type="button"
                                  onClick={handleClickPriceDetail}
                                >
                                  Back
                                </Button>
                              </div>
                            }
                          />
                        )}
                      </div>
                    ) : (
                      <>
                        <Dropdown
                          label="Price Point"
                          id="pricePoint"
                          options={defaultDropdownPriceOptions}
                          value={selectedPrice}
                          onChange={handlePriceSelect}
                        />
                        {/* More Price Options link */}
                        <div className={styles.priceOptionsLink}>
                          {showTooltipInfo && isClicked ? (
                            <div className={styles.tooltipInfoContainer}>
                              <div className={styles.icon}>
                                <Icon iconName="Info" className={styles.tooltipColor} />
                              </div>
                              <span className={styles.priceText}>
                                {t('editBundle.bundleSettings.editModal.useDifferentPrice?')}
                              </span>
                            </div>
                          ) : !showTooltipInfo && isClicked && showEditModal ? (
                            <Modal
                              data-testid="morePriceOptionsModal"
                              className={styles.editBundleModal}
                              isOpen={isOpen}
                              onClose={undefined}
                              headerContent={t('editBundle.bundleSettings.editModal.availablePricePoints')}
                              showTitleDivider
                              bodyContent={
                                <div style={{ minHeight: '435px' }}>
                                  <div className={styles.currentSettings}>
                                    <div className={styles.currentSettingsTitle}>
                                      {t('editBundle.bundleSettings.table.pricePoint')}
                                    </div>
                                    <div className={styles.recommendedPriceList}>
                                      {recommendedPriceList.map((price, idx) => (
                                        <div
                                          key={price}
                                          className={classNames(styles.blockContent, {
                                            // include a divider between sections if it's not the only block or not the last block
                                            [styles.rightBorder]:
                                              recommendedPriceList.length !== 1 &&
                                              idx !== recommendedPriceList.length - 1,
                                            [styles.maxWidth]: recommendedPriceList.length > 1,
                                          })}
                                        >
                                          {price}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  <div className={styles.bodyDivider} />
                                  <div className={styles.otherPricesTitle}>
                                    {t('editBundle.bundleSettings.editModal.otherAvailablePricePoints')}
                                  </div>
                                  <div className={styles.radioPricesContainer}>
                                    <div className={styles.radioPrices}>
                                      <RadioGroup
                                        label=""
                                        options={availablePriceOptions || []}
                                        onChange={onOtherAvailablePriceRadioChange}
                                      />
                                      <div className={styles.pricesInfo}>
                                        <div className={styles.icon}>
                                          <Icon iconName="Info" className={styles.tooltipColor} />
                                        </div>
                                        <div className={styles.priceText}>
                                          {t('editBundle.bundleSettings.editModal.useDifferentPrice?')}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              }
                              footerContent={
                                <div className={styles.footer1}>
                                  {/* <div className={styles.footerContainer1}> */}
                                  <div className={styles.modalButtons}>
                                    <Button
                                      variant={ButtonVariants.Secondary}
                                      onClick={closeSelectPriceModal}
                                      type="button"
                                    >
                                      Back
                                    </Button>
                                    <Button
                                      onClick={handleOtherAvailablePriceSelect}
                                      type="button"
                                      disabled={otherAvailablePrice === undefined}
                                    >
                                      Select Price
                                    </Button>
                                  </div>
                                  {/* </div> */}
                                </div>
                              }
                            />
                          ) : (
                            <Link to="" onClick={handleClick} data-testid="morePriceOptionsLink">
                              More Price Options
                            </Link>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className={redeemableOnlineLockStyles}>
                    <RadioGroup
                      lockIcon={redeemableLockIcon}
                      label={t('editBundle.bundleSettings.editModal.redeemableOnline')}
                      options={[
                        { label: t('common.yes'), id: 'redeemable_yes' },
                        { label: t('common.no'), id: 'redeemable_no' },
                      ]}
                      selected={isReemableOnlineSelected()}
                      onChange={onRedeemableOnlineRadioChange}
                      disabled={isLockRedeemableOnline || (visibleOnline === true && redeemableOnline === true)}
                    />
                  </div>
                  <div className={containerClassName}>
                    <RadioGroup
                      lockIcon={visibleLockIcon}
                      label={t('editBundle.bundleSettings.editModal.visibleOnline')}
                      options={[
                        { label: t('common.yes'), id: 'visible_yes' },
                        { label: t('common.no'), id: 'visible_no' },
                      ]}
                      selected={isVisibleOnlineSelected()}
                      onChange={onVisibleOnlineRadioChange}
                      disabled={redeemableOnline === false || isLockVisibleOnline}
                    />
                  </div>
                </div>
              </div>
            </div>
          }
          footerContent={
            <div className={styles.footer}>
              <div>
                {hasMissingItems && <MissingItemsAlert className={styles.missingItemsAlert} />}
                {
                  <CalendarAlert
                    className={styles.calendarAlert}
                    requestedVerbiage={false}
                    warningIcon={true}
                    rovoMessaging={!(isLockRedeemableOnline && isLockVisibleOnline)}
                  ></CalendarAlert>
                }
                <div className={styles.buttonContainer}>
                  <Button onClick={onClose} variant={ButtonVariants.Secondary}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={onConfirm}
                    isLoading={isLoading}
                    disabled={checkVisibility()}
                    disableOnLoading={true}
                  >
                    {t('common.confirm')}
                  </Button>
                </div>
              </div>
            </div>
          }
        />
      )}
    </>
  );
};

export default EditBundleSettingsModal;
